import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import useAxios from "axios-hooks";
import {
    Spinner,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    TextField,
    Toggle
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import ActionPlanAnalysisTable from "./ActionPlanAnalysisTable";
import { Form, Formik } from "formik";
import CustomTextField from "../../../../Forms/TextField";
import CustomToggle from "../../../../Forms/Toggle";

const ActionPlanAnalysisList = ({ crsId, revId }) => {
    const [selectedAnalysisId, setSelectedAnalysisId] = useState(undefined);

    const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);
    const [showCreateModal, { toggle: toggleCreateModal }] = useBoolean(false);
    const [showUpdateModal, { toggle: toggleUpdateModal }] = useBoolean(false);

    const [{ data: course, loading }, refetch] = useAxios(`courses/${crsId}/${revId || ''}`);

    if (loading)
        return <Spinner className="absolute-center" label="Loading data..." />;

    return (
        <>
            <ActionPlanAnalysisTable
                analyses={course.analyses}
                setSelectedAnalysisId={setSelectedAnalysisId}
                selectedAnalysisId={selectedAnalysisId}
                createOnClick={() => toggleCreateModal()}
                deleteOnClick={(analysis) => {
                    if (analysis && analysis.crsapAnalysisID !== undefined) {
                        setSelectedAnalysisId(analysis.crsapAnalysisID);
                        toggleDeleteModal();
                    } else if (selectedAnalysisId) {
                        toggleDeleteModal();
                    }
                }}
                editOnClick={(analysis) => {
                    setSelectedAnalysisId(analysis.crsapAnalysisID);
                    toggleUpdateModal();
                }}
                loading={loading}
                createDisabled={revId && revId != course.currentCRSRevID}
            />
            <CreateAnalysisModal
                courseId={crsId}
                showModal={showCreateModal}
                toggleShowModal={toggleCreateModal}
                onCreate={() => refetch()}
            />
            <UpdateAnalysisModal
                courseId={crsId}
                course={course}
                selectedAnalysisId={selectedAnalysisId}
                showModal={showUpdateModal}
                toggleShowModal={toggleUpdateModal}
                onUpdate={() => refetch()}
            />
            <DeleteAnalysisModal
                courseId={crsId}
                selectedAnalysisId={selectedAnalysisId}
                showModal={showDeleteModal}
                toggleShowModal={toggleDeleteModal}
                onDelete={() => refetch()}
            />
        </>
    );
};

const CreateAnalysisModal = ({
    showModal,
    toggleShowModal,
    onCreate,
    courseId
}) => {
    const [{ loading }, postForm] = useAxios(
        {
            method: "post",
            url: `/courses/${courseId}/action-plan-analysis`,
        },
        { manual: true }
    );
    if (loading)
        return <Spinner className="absolute-center" label="Loading..." />;

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Create",
                subText: "Analysis"
            }}
        >
            <Formik
                initialValues={{}}
                onSubmit={async (values) => {
                    const result = await postForm({ data: values });

                    if (!result.isAxiosError) {
                        onCreate();
                        toggleShowModal();
                    }
                }}
            >
                <Form>
                    <CustomTextField label="Sentence" name="sentence" required />
                    <CustomTextField label="Order Number" name="orderNumber" required />
                    <CustomToggle className="mt10" label="Inclusion/Exclusion (Default)*" name="onOffDefault"  onText="Include" offText="Exclude"/>
                    <CustomTextField label="Exception Rule*" name="onOffRule" />
                    <DialogFooter>
                        <PrimaryButton
                            text="Create"
                            type="submit"
                        />
                        <DefaultButton
                            loading={loading}
                            onClick={toggleShowModal}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Form>
            </Formik>
        </Dialog>
    );
};

const UpdateAnalysisModal = ({
    showModal,
    toggleShowModal,
    onUpdate,
    courseId,
    course,
    selectedAnalysisId
}) => {
    const [sentence, setSentence] = useState(undefined);
    const [orderNumber, setOrderNumber] = useState(undefined);
    const [onOffDefault, setOnOffDefault] = useState(undefined);
    const [onOffRule, setOnOffRule] = useState(undefined);

    useEffect(() => {
        const analysis = course?.analyses.find(s => s.crsapAnalysisID === selectedAnalysisId);
        setSentence(analysis?.sentence);
        setOrderNumber(analysis?.orderNumber);
        setOnOffDefault(analysis?.onOffDefault);
        setOnOffRule(analysis?.onOffRule);
    }, [selectedAnalysisId])

    const [{ loading }, update] = useAxios(
        {
            method: "put",
            url: `/courses/${courseId}/action-plan-analysis/${selectedAnalysisId}`,
        },
        { manual: true }
    );

    if (loading)
        return <Spinner className="absolute-center" label="Loading..." />;

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Update",
                subText: "Analysis"
            }}
        >
            <TextField label="Sentence" name="sentence" defaultValue={sentence} onChange={(e, newValue) => setSentence(newValue)} required />
            <TextField label="Order Number" name="orderNumber" defaultValue={orderNumber} onChange={(e, newValue) => setOrderNumber(newValue)} required />
            <Toggle className="mt10" label="Inclusion/Exclusion (Default)" name="onOffDefault"  onText="Include" offText="Exclude" defaultChecked={onOffDefault} onChange={(e, newValue) => setOnOffDefault(newValue)} />
            <TextField label="Exception Rule" name="onOffRule" defaultValue={onOffRule} onChange={(e, newValue) => setOnOffRule(newValue)} />
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await update({
                            data: {
                                crsapAnalysisID: selectedAnalysisId,
                                sentence,
                                orderNumber,
                                onOffDefault,
                                onOffRule,
                            }
                        });

                        if (!result.isAxiosError) {
                            onUpdate();
                            toggleShowModal();
                        }
                    }}
                    text="Update"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};

const DeleteAnalysisModal = ({
    showModal,
    toggleShowModal,
    onDelete,
    courseId,
    selectedAnalysisId
}) => {

    const [{ loading }, deleteItem] = useAxios(
        {
            method: "delete",
            url: `/courses/${courseId}/action-plan-analysis/${selectedAnalysisId}`,
        },
        { manual: true }
    );

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Delete",
                subText: "Delete Analysis?"
            }}
        >
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await deleteItem();

                        if (!result.isAxiosError) {
                            onDelete();
                            toggleShowModal();
                        }
                    }}
                    text="Delete"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};



ActionPlanAnalysisList.propTypes = {
    crsId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    revId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ])
};

// Modal components propTypes
const modalCommon = {
    showModal: PropTypes.bool.isRequired,
    toggleShowModal: PropTypes.func.isRequired,
};

CreateAnalysisModal.propTypes = {
    ...modalCommon,
    courseId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    onCreate: PropTypes.func.isRequired,
};

UpdateAnalysisModal.propTypes = {
    ...modalCommon,
    courseId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    course: PropTypes.object.isRequired,
    selectedAnalysisId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    onUpdate: PropTypes.func.isRequired,
};

DeleteAnalysisModal.propTypes = {
    ...modalCommon,
    courseId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    selectedAnalysisId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    onDelete: PropTypes.func.isRequired,
};

export default ActionPlanAnalysisList;